<template>
  <div class="adviser-business-card">
    <f-alert
      theme="primary"
      visible
      class="mb-2">
      {{ $t('adviserSettingsBusinessCardInfo.yourDataWillBePublic') }}
    </f-alert>

    <div class="card is-smallshadow">
      <div class="card-body py-3">
        <h2 class="heading-small text-grey mb-2">
          {{ $t('adviserSettings.myExpertCard') }}
        </h2>

        <validation-observer
          ref="adviser-settings-business-card-form"
          tag="form">

          <div class="form-group">
            <p class="text-medium">{{ $t('adviserSettingsBusinessCardInfo.photo') }}</p>

            <template v-if="data.image">
              <img
                class="profie-image mb-1 mt-1"
                :src="fileSrc" />
              <div
                v-if="data.image"
                class="text-left pt-1">
                <f-button
                  theme="danger"
                  @click.prevent="removeAdviserImage()">
                  {{ $t('adviserSettingsBusinessCardInfo.deletePhoto') }}
                </f-button>
              </div>
            </template>
            <template v-else>
              <f-file-input
                v-if="!data.image"
                :onChange="setImage"
                accept=".jpg, .jpeg, .png"
                id="inputFileAdviserImage" />
              <p
                v-if="isIncorrectFileFormat"
                class="text-danger">
                {{ $t('adviserSettingsBusinessCardInfo.itsNotAPhoto') }}
              </p>

              <div class="cropper-wrapper flex flex-wrap">
                <div class="flex width-narrow flex-wrap mb-3 mr-4 mr-3-md">
                  <div
                    :class="{
                      'croppper-content ': true,
                      'is-hidden': !sourceFileBase64
                    }">
                    <section class="cropper-area">
                      <div class="img-cropper">
                        <vue-cropper
                          ref="cropper"
                          :aspect-ratio="1 / 1"
                          :src="sourceFileBase64"
                          preview=".preview"
                          :responsive="true"
                        />
                      </div>
                      <div
                        v-if="sourceFileBase64"
                        class="actions flex flex-wrap flex-justify-content-center mt-1">
                        <f-button
                          theme="primary"
                          class="mx-1 my-1"
                          @click.prevent="cropImage">
                          {{ $t('adviserSettingsBusinessCardInfo.cut') }}
                        </f-button>
                        <f-button
                          theme="warning"
                          class="mx-1 my-1"
                          @click.prevent="reset">
                          {{ $t('adviserSettingsBusinessCardInfo.reset') }}
                        </f-button>
                      </div>
                    </section>
                  </div>
                </div>
                <section
                  v-if="sourceFileBase64"
                  class="preview-area">
                  <p class="text-medium mt-0">{{ $t('adviserSettingsBusinessCardInfo.preview') }}</p>
                  <div class="preview"></div>
                </section>
              </div>
            </template>
          </div>

          <div class="width-narrow">
            <div class="form-group">
              <label for="work-start-date">{{ $t('adviserSettingsBusinessCardInfo.dateStartWork') }}</label>
              <validation-provider
                name="work-start-date"
                v-slot="{ errors }">
                <div
                  :class="{
                    'is-date-range': true,
                    'vs-wrap-text': true,
                    'is-invalid': errors.length
                  }">
                  <v-date-picker
                    v-model="data.work_start_date"
                    mode="date"
                    :locale="$i18n.locale"
                    class="date">
                    <template v-slot="{ inputValue, togglePopover }">
                      <input
                        :value="inputValue"
                        @click="togglePopover()"
                        class="form-control f-input" />
                    </template>
                  </v-date-picker>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </div>
              </validation-provider>
            </div>

            <div class="form-group">
              <label for="experience">{{ $t('adviserSettings.workExperience') }}</label>

              <validation-provider
                name="experience"
                v-slot="{ errors }">
                <f-input-wrapper :append="$t('ui.years')">
                  <f-input
                    v-model="data.years_of_experience"
                    min="0"
                    step="1"
                    type="number"
                    :state="errors.length ? 'invalid' : null"/>
                </f-input-wrapper>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group">
              <label for="description">{{ $t('adviserSettingsBusinessCardInfo.description') }}</label>

              <validation-provider
                name="description"
                v-slot="{ errors }">
                <f-textarea
                  v-model="data.description"
                  :rows="6"
                  :state="errors.length ? 'invalid' : null"
                  :maxLength="800"/>
                <div class="additional-info">
                  <f-icon name="info" />
                  <span>{{ $t('adviserSettingsBusinessCardInfo.thisTextWillBeVisibleOnYourCard') }}</span>
                </div>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group">
              <label for="short-description">{{ $t('adviserSettingsBusinessCardInfo.littleDescription') }}</label>

              <validation-provider
                name="short-description"
                v-slot="{ errors }">
                <f-textarea
                  v-model="data.short_description"
                  :rows="3"
                  :state="errors.length ? 'invalid' : null"
                  :maxLength="180" />
                <div class="additional-info">
                  <f-icon name="info" />
                  <span>{{ $t('adviserSettingsBusinessCardInfo.thisTextWillBeVisibleUnderYourSurname') }}</span>
                </div>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group">
              <label for="adviser-status">{{ $t('user.status') }}</label>

              <validation-provider
                rules="required"
                name="adviser-status"
                v-slot="{ errors }">
                <div
                  :class="{
                    'form-select': true,
                    'is-invalid': errors.length
                  }">
                  <v-select
                    :options="adviserStatuses"
                    v-model="data.adviser_status"
                    :reduce="(status) => status.value"
                    :placeholder="$t('user.selectStatus')"
                    label="text">
                    <template slot="no-options">
                      {{ $t('ui.noResultsForGivenPhrase') }}
                    </template>
                  </v-select>
                </div>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group">
              <label for="specialisation">{{ $t('user.specialization') }}</label>

              <validation-provider
                rules="required"
                name="specialisation"
                v-slot="{ errors }">
                <div
                  :class="{
                    'form-select': true,
                    'is-invalid': errors.length
                  }">
                  <v-select
                    :options="allSpecialisations"
                    v-model="data.adviser_specialisation_id"
                    :reduce="(specialisation) => specialisation.id"
                    :placeholder="$t('user.selectSpecialization')"
                    label="name">
                    <template slot="no-options">
                      {{ $t('ui.noResultsForGivenPhrase') }}
                    </template>
                  </v-select>
                </div>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group">
              <label for="additional-specialisations">{{ $t('user.additionalSpecializations') }} (max: {{ maxSpecialisationsCount }})</label>

              <validation-provider
                rules=""
                name="additional-specialisations"
                v-slot="{ errors }">
                <div
                  :class="{
                    'form-select': true,
                    'is-invalid': errors.length
                  }">
                  <v-select
                    v-model="data.specialisations"
                    :options="allSpecialisations"
                    :multiple="true"
                    :reduce="(specialisation) => specialisation.id"
                    :placeholder="$t('user.selectSpecializations')"
                    :selectable="specialisation => specialisation.id !== data.adviser_specialisation_id && data.specialisations.length < maxSpecialisationsCount"
                    label="name">
                    <template slot="no-options">
                      {{ $t('ui.noResultsForGivenPhrase') }}
                    </template>
                  </v-select>
                </div>
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group" v-if="langId !== 2">
                <label for="adviser-association">{{ $t('user.association') }}</label>
                <validation-provider
                  tag="div"
                  rules="required"
                  name="adviser-association"
                  v-slot="{ errors }">
                  <div
                    :class="{
                      'form-select': true,
                      'is-invalid': errors.length
                    }">
                    <v-select
                      id="adviser-association"
                      :options="allAssociations"
                      v-model="association"
                      :placeholder="$t('user.selectAssociation')"
                      label="name">
                      <template slot="no-options">
                        {{ $t('ui.noResultsForGivenPhrase') }}
                      </template>
                    </v-select>
                  </div>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div
                v-if="data.association_id === 0"
                class="form-group">
                <label for="association-other">{{ $t('user.otherAssociation') }}</label>

                <validation-provider
                  rules="required"
                  name="association-other"
                  v-slot="{ errors }">
                  <f-input
                    v-model="data.association"
                    :state="errors.length ? 'invalid' : null"/>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
          </div>
        </validation-observer>
      </div>
      <div class="card-footer pt-3 pb-4 text-right">
        <f-button
          theme="primary"
          big
          @click.prevent="validateForm">
          {{ $t('ui.save') }}
        </f-button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';
import VueCropper from 'vue-cropperjs';
import AdviserStatuses from '@/data/adviser-statuses.js';
import { mapGetters } from 'vuex';


export default {
  name: 'adviser-settings-business-card-info',
  components: {
    VueCropper
  },
  props: {
    adviserData: {
      default: () => {},
      type: Object
    },
    allAssociations: {
      type: Array
    },
    allSpecialisations: {
      type: Array
    },
    maxSpecialisationsCount: {
      default: 0,
      type: Number
    }
  },
  watch: {
    'data.adviser_specialisation_id': function (newValue) {
      if (!this.data.specialisations.length) {
        return;
      }

      let specIndex = this.data.specialisations.indexOf(newValue);

      if (specIndex > -1) {
        this.data.specialisations.splice(specIndex, 1);
        this.allSpecialisations.find(item => item.id === newValue).disabled = true;
      }
    }
  },
  computed: {
    ...mapGetters({
      langId: 'getUserLanguageId'
    }),
    fileSrc () {
      if (!this.data.image) {
        return null;
      }

      if (!this.data.image_url) {
        return URL.createObjectURL(this.data.image);
      }

      return this.data.image_url;
    },
    adviserStatuses () {
      return AdviserStatuses;
    },
    association: {
      set(association) {
        this.data.association_id = association.id;
        this.data.association = association.name;
      },
      get() { return this.data.association }
    }
  },
  data() {
    return {
      cropImg: '',
      data: {
        adviser_specialisation_id: null,
        adviser_status: null,
        association: '',
        association_id: null,
        description: null,
        image: null,
        image_url: null,
        short_description: null,
        specialisations: [],
        work_start_date: null,
        years_of_experience: 0
      },
      isIncorrectFileFormat: false,
      isVideoRemoved: false,
      loaded: false,
      newFileName: '',
      sourceFileBase64: null
    }
  },
  mounted() {
    this.data = JSON.parse(JSON.stringify(this.adviserData));
  },
  methods: {
    submitForm () {
      let formDataToSend = {
        adviser_specialisation_id: this.data.adviser_specialisation_id,
        adviser_status: this.data.adviser_status,
        association_id: this.data.association_id,
        description: this.data.description,
        short_description: this.data.short_description,
        specialisations: this.data.specialisations,
        work_start_date: this.prepareDateString(this.data.work_start_date),
        years_of_experience: this.data.years_of_experience ? this.data.years_of_experience : 0
      };

      if (this.data.association_id === 0) {
        formDataToSend.association = this.data.association;
      }

      if (this.isVideoRemoved && !(this.data.image && this.data.image.length)) {
        formDataToSend.remove_image = 1;
      }

      if (this.data.image && !this.data.image_url) {
        formDataToSend.image = this.data.image;
      }

      let fileParams = [];
      if (formDataToSend.image) {
        fileParams.push('file');
      }

      FormUtils.submit(this, {
        endpoint: '/api/adviser/adviser/update-card',
        formData: formDataToSend,
        successTitle: this.$t('ui.savedChanges'),
        successText: this.$t('adviserSettingsBusinessCardInfo.infoAboutYourCustomerCardHasBeenSaved'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('adviserSettings.yourDataModifyError'),
        successAction: (response) => {
          this.$bus.$emit('adviser-settings-reload');
          Vue.set(this.data, 'adviser_status', response.item.adviser_status);
          Vue.set(this.data, 'description', response.item.description);
          Vue.set(this.data, 'image', response.item.image);
          Vue.set(this.data, 'image_url', response.item.image_url);
          Vue.set(this.data, 'short_description', response.item.short_description);
          Vue.set(this.data, 'adviser_specialisation_id', response.item.adviser_specialisation_id);
          Vue.set(this.data, 'work_start_date', response.item.work_start_date);
          Vue.set(this.data, 'years_of_experience', response.item.years_of_experience);
          Vue.set(this.data, 'association_id', response.item.association_id);
          Vue.set(this.data, 'association', response.item.association);
          Vue.set(this.data, 'specialisations', response.item.specialisations);
          Vue.set(this, 'isVideoRemoved', false);
        }
      }, fileParams);
    },
    validateForm () {
      FormUtils.validate(this.$refs['adviser-settings-business-card-form'], this.submitForm);
    },
    setAdviserImage (event) {
      Vue.set(this.data, 'image', event.target.files[0]);
    },
    removeAdviserImage () {
      Vue.set(this, 'isVideoRemoved', true);
      Vue.set(this.data, 'image_url', '');
      Vue.set(this.data, 'image', '');
    },
    prepareDateString (input) {
      if (input === null) {
        return '-';
      }
      input = new Date(input);

      return [
        input.getFullYear(),
        input.getMonth() + 1,
        input.getDate()
      ]
        .map(n => n < 10 ? '0' + n : n)
        .join('-');
    },
    cropImage() {
      this.cropImg = this.$refs.cropper.getCroppedCanvas({
        width: 200,
        height: 200
      }).toDataURL();
      Vue.nextTick(() => {
        Vue.set(this.data, 'image', this.getBase64File(this.cropImg, new Date().getTime() + this.newFileName));
      })
    },
    getBase64File (dataurl, filename) {
      let dataArray = dataurl.split(',');
      let mimeType = dataArray[0].match(/:(.*?);/)[1];
      let bitString = atob(dataArray[1]);
      let arrayLength = bitString.length;
      let u8array = new Uint8Array(arrayLength);

      while (arrayLength--) {
        u8array[arrayLength] = bitString.charCodeAt(arrayLength);
      }

      return new File([u8array], filename, { type: mimeType });
    },
    reset() {
      this.$refs.cropper.reset();
    },
    setImage(e) {
      let file = e.target.files[0];
      Vue.set(this, 'isIncorrectFileFormat', false);
      if (file.type.indexOf('image/') === -1) {
        Vue.set(this, 'isIncorrectFileFormat', true);
        return;
      }

      this.newFileName = e.target.files[0].name;
      if (typeof FileReader === 'function') {
        const reader = new FileReader();
        reader.onload = (event) => {
          this.sourceFileBase64 = event.target.result;
          this.$refs.cropper.replace(event.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        console.log('Sorry, FileReader API not supported');
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';
.profie-image {
  border-radius: 10px;
  display: block;
  height: auto;
  max-width: 100%;
}

input[type="file"] {
  display: none;
}

.is-hidden {
  visibility: hidden;
  height: 0;
}

.preview-area {
  width: 200px;
}
.preview {
  border-radius: 10px;
  height: 200px;
  overflow: hidden;
  width: 200px;

  img {
    display: block;
    height: 100%;
    widows: 100%;
  }
}
.crop-placeholder {
  width: 100%;
  height: 200px;
  background: #ccc;
}
.cropped-image img {
  max-width: 100%;
}

.cropper-area {
  max-width: 100%;
}

.cropper-container {
  max-width: 100%;
}

.croppper-content {
  width: 100%;
  max-width: 100%;
}

.cropper-wrapper {
  max-width: 100%;
}

.adviser-business-card .width-narrow {
  width: $dimension-width-narrow;
  max-width: 100%;
}
</style>
